import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { NotificationTemplateItem } from 'src/app/models/NotificationConfig';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { ProjectsService } from './projects.service';
import { NotificationType, NotificationTypeLabels } from 'src/app/models/NotificationType';
import { Notice } from 'src/app/models/Notice';
import { NotificationUserConfiguration } from 'src/app/models/NotificationUserConfiguration';

interface NotificationsQuery {
  userId: string;
  type?: string;
  priority?: string;
  dateFrom?: string;
  dateTo?: string;
  read?: string;
  page?: number;
  size?: number;
}

interface NotificationsApiQuery extends NotificationsQuery {
  userId: string;
}

interface NotificationsResponse {
  content: Notice[];
  totalElements: number;
  page: number;
  size: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {

  private userId = ''
  private notificationsUrl = environment.notificationsUrl;
  private readonly unreadCountChangedSubject = new Subject<void>();
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private unreadCountPollingSubscription: Subscription | null = null;
  private readonly unreadCountPollingIntervalMs = 60000;

  readonly unreadCountChanged$ = this.unreadCountChangedSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private projectsService: ProjectsService,
    private permissionService: PermissionService
  ) 
  { 
    this.getUserId();
    this.initializeUnreadCountPolling();
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + this.authService.getToken()
    });
  }

  private getUserId() {
    this.authService.getUserData().subscribe(user => {
      this.userId = user?.id || '';
    });
  }

  private initializeUnreadCountPolling(): void {
    this.authService.getUserData().pipe(
      map(user => user?.id || ''),
      distinctUntilChanged()
    ).subscribe((userId) => {
      this.userId = userId;

      if (!userId) {
        this.stopUnreadCountPolling();
        this.unreadCountSubject.next(0);
        return;
      }

      this.startUnreadCountPolling(userId);
    });
  }

  private startUnreadCountPolling(userId: string): void {
    this.stopUnreadCountPolling();

    this.unreadCountPollingSubscription = timer(0, this.unreadCountPollingIntervalMs).pipe(
      switchMap(() => this.refreshUnreadCountNow(userId))
    ).subscribe();
  }

  private stopUnreadCountPolling(): void {
    if (this.unreadCountPollingSubscription) {
      this.unreadCountPollingSubscription.unsubscribe();
      this.unreadCountPollingSubscription = null;
    }
  }

  getNotificationsApi(query?: NotificationsApiQuery): Observable<NotificationsResponse> {
    return this.http.post<NotificationsResponse>(this.notificationsUrl + '/search', query, { headers: this.getHeaders() } );
  }

  markAsReadApi(id: string): Observable<any> {
    return this.http.put<any>(this.notificationsUrl + '/' + encodeURIComponent(id) + '/read', {userId: this.userId}, {
      headers: this.getHeaders()
    });
  }

  getUnreadCountApi(userId: string): Observable<number> {
    return this.http.get<number>(this.notificationsUrl + '/unread-count/' + encodeURIComponent(userId), {
      headers: this.getHeaders()
    });
  }

  refreshUnreadCountNow(userId?: string): Observable<number> {
    const targetUserId = userId || this.userId;

    if (!targetUserId) {
      this.unreadCountSubject.next(0);
      this.notifyUnreadCountChanged();
      return of(0);
    }

    return this.getUnreadCountApi(targetUserId).pipe(
      catchError((error) => {
        console.error('Error fetching unread count:', error);
        return of(0);
      }),
      map((count) => count || 0),
      tap((count) => {
        this.unreadCountSubject.next(count);
        this.notifyUnreadCountChanged();
      })
    );
  }

  notifyUnreadCountChanged(): void {
    this.unreadCountChangedSubject.next();
  }

  getNotificationUserConfiguration(): Observable<any> {
    return this.http.get<NotificationUserConfiguration[]>(
      this.notificationsUrl + '/configuration/' + encodeURIComponent(this.userId),
      { headers: this.getHeaders() }
    );
  }

  updateNotificationUserConfiguration(items: NotificationUserConfiguration[]): Observable<NotificationUserConfiguration[]> {
    return this.http.put<NotificationUserConfiguration[]>(
      this.notificationsUrl + '/configuration/' + encodeURIComponent(this.userId),
      items,
      { headers: this.getHeaders() }
    );
  }
}